/**
 * @fileoverview Renders one structured teaching guide — the title, its type
 * chip and the sectioned body Hans produced. Shared by the repertoire
 * assistant (a fresh result, a saved guide reopened, a compact history
 * block) and the saved-guides library, so a guide reads the same wherever it
 * is opened from.
 */

import { motion } from "framer-motion";
import { BookOpen } from "lucide-react";

/** Section icon mapping for visual distinction. */
const SECTION_ICONS = {
  "overview": "📋",
  "key teaching points": "🎯",
  "common challenges": "⚠️",
  "practice approach": "📝",
  "tips": "💡",
  "student-specific notes": "👤",
};

const getSectionIcon = (heading) => {
  const key = (heading || "").toLowerCase();
  for (const [match, icon] of Object.entries(SECTION_ICONS)) {
    if (key.includes(match)) return icon;
  }
  return "📌";
};

/** Markdown → HTML: supports bold, italic, bullets, numbered lists, sub-headers. */
export function formatMarkdown(text) {
  if (!text) return "";
  // Handle non-string content (arrays, objects) from AI responses
  if (Array.isArray(text)) text = text.map((item) => (typeof item === "string" ? item : JSON.stringify(item))).join("\n");
  if (typeof text !== "string") text = String(text);
  // Escape HTML
  let html = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Bold
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  // Italic
  html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");

  const lines = html.split("\n");
  let inUl = false;
  let inOl = false;
  const processed = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // Numbered list: "1. ", "2. ", etc.
    const olMatch = trimmed.match(/^(\d+)\.\s+(.*)$/);
    // Bullet list: "- " or "* "
    const ulMatch = !olMatch && (trimmed.startsWith("- ") || trimmed.startsWith("* "));
    // Sub-header: "### " or "#### "
    const headerMatch = trimmed.match(/^#{3,4}\s+(.*)$/);

    if (ulMatch) {
      if (inOl) { processed.push("</ol>"); inOl = false; }
      if (!inUl) { processed.push("<ul>"); inUl = true; }
      processed.push(`<li>${trimmed.slice(2)}</li>`);
    } else if (olMatch) {
      if (inUl) { processed.push("</ul>"); inUl = false; }
      if (!inOl) { processed.push("<ol>"); inOl = true; }
      processed.push(`<li>${olMatch[2]}</li>`);
    } else if (headerMatch) {
      if (inUl) { processed.push("</ul>"); inUl = false; }
      if (inOl) { processed.push("</ol>"); inOl = false; }
      processed.push(`<h5>${headerMatch[1]}</h5>`);
    } else {
      if (inUl) { processed.push("</ul>"); inUl = false; }
      if (inOl) { processed.push("</ol>"); inOl = false; }
      if (trimmed) processed.push(`<p>${trimmed}</p>`);
    }
  }
  if (inUl) processed.push("</ul>");
  if (inOl) processed.push("</ol>");
  return processed.join("");
}

/**
 * Render structured teaching guide sections.
 *
 * @param {{ result: { title: string, type?: string, sections?: Array<{heading: string, content: any}> }, compact?: boolean }} props
 *   `compact` skips the per-section entrance stagger (history blocks).
 */
export function RepertoireGuide({ result, compact }) {
  return (
    <div className="flex flex-col gap-4" data-testid="repertoire-guide">
      <div className="mb-2 flex items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--accent-yellow-dim)]">
          <BookOpen className="h-4 w-4 text-[var(--accent-yellow-text)]" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="font-medium text-[var(--text-primary)]">{result.title}</h3>
          {result.type && (
            <span className="rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-faint)] px-2 py-0.5 text-xs text-[var(--accent-yellow-text)]">
              {result.type}
            </span>
          )}
        </div>
      </div>
      {result.sections?.map((section, idx) => (
        <motion.div
          key={idx}
          initial={compact ? {} : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: compact ? 0 : idx * 0.1 }}
          className="rounded-xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-5 transition-colors hover:border-[var(--studio-divider-strong)]"
        >
          <div className="mb-3 flex items-center gap-2">
            <span className="text-base">{getSectionIcon(section.heading)}</span>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--accent-yellow-text)]">
              {section.heading}
            </h4>
          </div>
          <div
            className="max-w-none text-sm leading-relaxed text-[var(--text-secondary)]
                       [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ul]:my-2
                       [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1.5 [&_ol]:my-2
                       [&_li]:leading-relaxed
                       [&_strong]:text-[var(--text-primary)] [&_strong]:font-semibold
                       [&_p]:mb-2 [&_p:last-child]:mb-0
                       [&_h5]:text-[var(--text-primary)] [&_h5]:font-medium [&_h5]:text-sm [&_h5]:mt-3 [&_h5]:mb-1"
            dangerouslySetInnerHTML={{ __html: formatMarkdown(section.content) }}
          />
        </motion.div>
      ))}
    </div>
  );
}

export default RepertoireGuide;
